import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { Button } from "@mantine/core";
import { consumeSsoToken, redirectToSso, redirectToSsoLogout, clearSsoRedirectState, consumeIntentionalLogout, } from "@labeling-tools/auth";
/**
 * Single source of truth for the client side of central SSO. Drop it around an
 * app's authenticated tree:
 *
 *   <SsoGate
 *     auth={auth}
 *     signIn={signIn}
 *     ssoLoginUrl={import.meta.env.VITE_SSO_LOGIN_URL}
 *     devMode={import.meta.env.DEV}
 *     appName="Out of Stock"
 *   >
 *     <App />
 *   </SsoGate>
 *
 * Behavior:
 *  - Completes an SSO round-trip if a token is in the URL fragment.
 *  - A token that fails to sign in is terminal (shows an error, never loops).
 *  - Signed-out users are sent to SSO; redirectToSso's attempt budget breaks
 *    runaway redirect loops and surfaces an error instead.
 *  - In dev with no SSO URL configured, shows a local popup sign-in.
 */
export function SsoGate({ auth, children, ssoLoginUrl, devMode = false, ssoEnabled = true, signIn, appName, }) {
    const [status, setStatus] = useState("loading");
    const [error, setError] = useState("");
    const [authedUser, setAuthedUser] = useState(null);
    const startedRef = useRef(false);
    useEffect(() => {
        if (startedRef.current)
            return;
        startedRef.current = true;
        let unsubscribe;
        const fail = (message) => {
            setError(message);
            setStatus("error");
        };
        // Complete a central-SSO round-trip (token arrives in the URL fragment)
        // before reading auth state, then route signed-out users appropriately.
        consumeSsoToken(auth).then((result) => {
            if (result === "failed") {
                // A token came back but sign-in failed (e.g. mismatched Firebase
                // project, expired token). Redirecting again would just mint another
                // token that fails the same way, so surface an error instead of looping.
                fail("We couldn't complete sign-in. Please try again, and let the team know if this keeps happening.");
                return;
            }
            unsubscribe = auth.onAuthStateChanged((user) => {
                if (user) {
                    setAuthedUser(user);
                    clearSsoRedirectState();
                    consumeIntentionalLogout(); // clear stale flag if somehow set during sign-in
                    setStatus("ready");
                    return;
                }
                // Deliberate sign-out: redirect to SSO with a logout hint so it clears
                // its own session instead of auto-signing the user back in.
                // Mirror the same condition as the normal SSO redirect below: fall back
                // to the default URL when ssoLoginUrl is unset (most apps omit it).
                if (consumeIntentionalLogout()) {
                    if (ssoEnabled && !(devMode && !ssoLoginUrl)) {
                        redirectToSsoLogout(ssoLoginUrl);
                    }
                    else {
                        setStatus("dev-login");
                    }
                    return;
                }
                // Local popup path: dev convenience, or SSO explicitly disabled via
                // the ssoEnabled flag (for pre-rollout prod deploys).
                if (!ssoEnabled || (devMode && !ssoLoginUrl)) {
                    setStatus("dev-login");
                    return;
                }
                // redirectToSso returns false once we've bounced too many times this
                // session without signing in — break the loop and surface a
                // recoverable error (the "Try again" button resets the budget).
                if (!redirectToSso(ssoLoginUrl)) {
                    fail("Sign-in isn't completing. Use Try again, and let the team know if this keeps happening.");
                }
            });
        });
        return () => unsubscribe?.();
    }, [auth, ssoLoginUrl, devMode, ssoEnabled]);
    if (status === "ready") {
        return (_jsx(_Fragment, { children: typeof children === "function"
                ? authedUser
                    ? children(authedUser)
                    : null
                : children }));
    }
    if (status === "dev-login") {
        return _jsx(DevSignIn, { appName: appName, signIn: signIn, devMode: devMode });
    }
    if (status === "error") {
        // Clear the redirect-attempt budget before reloading so "Try again"
        // actually gets a fresh round-trip instead of bouncing off the same
        // exhausted counter (which survives a plain refresh).
        const retry = () => {
            clearSsoRedirectState();
            window.location.reload();
        };
        return (_jsxs("div", { className: "flex flex-col h-screen items-center justify-center gap-4 px-6 text-center", children: [_jsx("h1", { className: "text-xl font-bold text-gray-900", children: "Sign-in problem" }), _jsx("p", { className: "text-gray-600 max-w-md", children: error }), _jsx(Button, { onClick: retry, children: "Try again" })] }));
    }
    // "loading" — blank while we resolve auth state, matching prior behavior.
    return null;
}
function DevSignIn({ appName, signIn, devMode, }) {
    const [error, setError] = useState("");
    const handleSignIn = async () => {
        try {
            await signIn?.();
        }
        catch (err) {
            console.error("Dev sign-in failed:", err);
            setError("Google sign-in was cancelled or failed.");
        }
    };
    return (_jsxs("div", { className: "flex flex-col h-screen items-center justify-center gap-5 px-6 text-center", children: [_jsx("h1", { className: "text-2xl font-bold text-gray-900", children: devMode ? `${appName ? `${appName} — ` : ""}Dev Sign In` : "Sign In" }), devMode && (_jsx("p", { className: "text-gray-500 max-w-md", children: "Local development sign-in. Production uses central SSO at accounts.augmoto.com." })), _jsx(Button, { onClick: handleSignIn, size: "lg", children: "Sign In with Google" }), error && _jsx("p", { className: "text-red-700 max-w-md", children: error })] }));
}
